// Editing a ticket that already exists.
//
// WHY THIS EXISTS. `TicketMeta` was entirely read-only, and `PATCH /api/tickets/:ref`
// took only status, priority, assignee and tags — so correcting a queue or a requester
// meant closing the ticket and raising a new one, which splits one conversation across
// two and loses the thread the customer sees. Being wrong about those is not rarer once
// the ticket exists; it is MORE likely, because the second call is when the agent finds
// out.
//
// ONE PANEL, NOT INLINE FIELDS, and the reason is the audit trail. Every written field
// writes a `ticket_events` row, so per-field autosave would turn one correction into
// six entries in the history an agent reads to understand a ticket. Collecting the
// changes and sending them together writes exactly what changed —
// `changedTicketFields` is what guarantees that, and it is pure and tested.
//
// NO CONFIRMATION DIALOG, deliberately. Nothing here is irreversible: every field is
// re-editable and every change is in the history. The customer panel confirms because
// moving somebody between accounts silently changes their BRAND and re-files every
// future ticket; nothing on this screen has a consequence the agent cannot see. The
// Save button naming what it will write is the affordance instead.

import { useEffect, useState } from 'react';

import { api, ApiError } from '../../api/client.js';
import TagInput from '../create/TagInput.jsx';
import ChipSelect from '../create/ChipSelect.jsx';
import { typeOptionsFor } from '../../pages/tickets/ticket-type.js';
import { changedTicketFields, draftFromTicket, describeTicketChanges } from './ticket-edit.js';

const BASE_STATUSES = [
  ['new', 'New'], ['open', 'Open'], ['pending', 'Pending'],
  ['on_hold', 'On hold'], ['solved', 'Solved'], ['closed', 'Closed'],
];

const PRIORITIES = [['low', 'Low'], ['normal', 'Normal'], ['high', 'High'], ['urgent', 'Urgent']];

export default function TicketEditPanel({ ticket, onSaved, onCancel }) {
  const saved = draftFromTicket(ticket);
  const [draft, setDraft] = useState(saved);
  const [config, setConfig] = useState({ assignees: [], users: [], queues: [], tags: [], people: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (field, value) => setDraft((previous) => ({ ...previous, [field]: value }));

  // Every list is loaded independently and every failure is swallowed into an empty
  // list, for the reason the create screen states: a lookup that fails should leave its
  // picker empty and the rest of the panel usable, rather than taking the screen down
  // over a dropdown. The one exception would be a list whose emptiness MEANS something,
  // and none of these has that property — an empty queue picker is obviously broken,
  // where an empty schedule roster is a claim about routing.
  useEffect(() => {
    const controller = new AbortController();
    const load = (path, key) => api.get(path, { signal: controller.signal })
      .then((body) => setConfig((previous) => ({ ...previous, [key]: body?.data ?? [] })))
      .catch(() => {});

    load('/users?can_be_assigned=true', 'assignees');
    load('/users', 'users');
    load('/queues', 'queues');
    load('/tags', 'tags');
    // The organization's people are the CC candidates, matching the create screen. A
    // one-off caller with no organization has none, which is correct rather than a
    // failure: there is nobody else at their site to copy in.
    if (ticket?.organization?.id) {
      load(`/organizations/${ticket.organization.id}/customers`, 'people');
    }

    return () => controller.abort();
  }, [ticket?.organization?.id]);

  const patch = changedTicketFields(saved, draft);
  const saveLabel = describeTicketChanges(patch);

  async function save() {
    if (!saveLabel) { onCancel(); return; }
    setBusy(true);
    setError(null);
    try {
      const updated = await api.patch(`/tickets/${ticket.public_ref}`, patch);
      onSaved(updated);
    } catch (err) {
      setError(err instanceof ApiError
        ? (err.details?.fields?.[0]?.message ?? err.message)
        : 'Could not save the ticket.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <h2 className="text-sm font-semibold text-ink">Edit ticket</h2>

      {error && (
        <div className="mt-3 rounded-lg border border-line-strong bg-sunken p-3 text-sm text-ink">
          {error}
        </div>
      )}

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="t-status">Status</label>
          <select id="t-status" className="field-input" value={draft.status}
                  onChange={(e) => set('status', e.target.value)}>
            {BASE_STATUSES.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="field-label" htmlFor="t-priority">Priority</label>
          <select id="t-priority" className="field-input" value={draft.priority}
                  onChange={(e) => set('priority', e.target.value)}>
            {PRIORITIES.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="field-label" htmlFor="t-type">Type</label>
          <select id="t-type" className="field-input" value={draft.type}
                  onChange={(e) => set('type', e.target.value)}>
            <option value="">Not categorised</option>
            {typeOptionsFor(saved.type).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="field-label" htmlFor="t-assignee">Assignee</label>
          <select id="t-assignee" className="field-input" value={draft.assigned_user_id}
                  onChange={(e) => set('assigned_user_id', e.target.value)}>
            <option value="">Unassigned</option>
            {config.assignees.map((u) => (
              <option key={u.id} value={String(u.id)}>{u.display_name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="field-label" htmlFor="t-queue">Queue</label>
          <select id="t-queue" className="field-input" value={draft.queue_id}
                  onChange={(e) => set('queue_id', e.target.value)}>
            <option value="">No queue</option>
            {config.queues.map((q) => (
              <option key={q.id} value={String(q.id)}>{q.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* THE REQUESTER, and only among people at the same organization. A free customer
          search here would let an agent re-point a ticket at somebody unrelated with one
          mis-click, and the organization travels with the requester — so the ticket
          would silently change account too. Moving a ticket to a different company is a
          rarer and larger act than fixing which colleague reported it, and it does not
          belong behind the same dropdown. */}
      {config.people.length > 0 && (
        <div className="mt-3">
          <label className="field-label" htmlFor="t-requester">Requester</label>
          <select id="t-requester" className="field-input" value={draft.customer_id}
                  onChange={(e) => set('customer_id', e.target.value)}>
            {config.people.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}{p.position ? ` — ${p.position}` : ''}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-ink-muted">
            People at {ticket.organization?.name}. To correct this contact&rsquo;s own details,
            open them under Customers.
          </p>
        </div>
      )}

      <div className="mt-4">
        <label className="field-label">Tags</label>
        <TagInput
          value={draft.tags}
          onChange={(tags) => set('tags', tags)}
          suggestions={config.tags.map((t) => t.name ?? t)}
        />
      </div>

      <div className="mt-4">
        <ChipSelect
          id="t-ccs"
          label="CCs"
          hint="Copied on every customer-facing reply. Removing somebody stops that."
          options={config.people.map((p) => ({ id: String(p.id), label: p.name }))}
          value={draft.cc_customer_ids}
          onChange={(ids) => set('cc_customer_ids', ids)}
          placeholder="Add a CC"
        />
      </div>

      <div className="mt-4">
        <ChipSelect
          id="t-followers"
          label="Followers"
          hint="Anyone can follow. Hardware and implementation get looped in this way."
          options={config.users.map((u) => ({ id: String(u.id), label: u.display_name }))}
          value={draft.follower_user_ids}
          onChange={(ids) => set('follower_user_ids', ids)}
          placeholder="Add a follower"
        />
      </div>

      <div className="mt-4 flex items-center gap-3">
        {/* The button NAMES what it will write. There is no confirmation in front of it
            and none is needed, but a Save that looks identical whether it changes one
            field or six invites the click that changes six. */}
        <button type="button" className="btn-primary" disabled={busy || !saveLabel}
                onClick={save}>
          {busy ? 'Saving…' : (saveLabel ?? 'No changes')}
        </button>
        <button type="button" className="btn-ghost ring-1 ring-line-strong" disabled={busy}
                onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
